import React, { useState, useEffect } from 'react';
import { 
  UserPlus, X, Copy, Check, Search, Edit2, 
  Shield, UserCog, Eye, FileText, User
} from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from '../ui/simple-toaster';

const ROLE_LABELS = {
  'SYSTEM_ADMIN': 'System Admin',
  'CLIENT_ADMIN': 'Admin',
  'SCHEDULER': 'Scheduler',
  'RECORD_KEEPER': 'Record Keeper',
  'VIEWER': 'Viewer',
  'DRIVER': 'Driver'
};

const ROLE_COLORS = {
  'SYSTEM_ADMIN': 'bg-purple-500/20 text-purple-400',
  'CLIENT_ADMIN': 'bg-blue-500/20 text-blue-400',
  'SCHEDULER': 'bg-emerald-500/20 text-emerald-400',
  'RECORD_KEEPER': 'bg-amber-500/20 text-amber-400',
  'VIEWER': 'bg-slate-500/20 text-slate-400',
  'DRIVER': 'bg-cyan-500/20 text-cyan-400'
};

const ROLE_DESCRIPTIONS = {
  'CLIENT_ADMIN': 'Full access to all features, can manage users and settings',
  'SCHEDULER': 'Can view drivers, create schedules, send alerts',
  'RECORD_KEEPER': 'Can view compliance reports and export data',
  'VIEWER': 'Read-only access to dashboard and basic reports'
};

const StaffPanel = ({ user }) => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  
  const [inviteForm, setInviteForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: 'VIEWER',
    phone: ''
  });

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    setLoading(true);
    try {
      // Get all users for this client (non-drivers)
      const driversRes = await api('/drivers');
      const driverUserIds = new Set((driversRes.drivers || []).map(d => d.user_id));
      
      // For now, we'll show staff based on the drivers endpoint
      // In a full implementation, we'd have a /users endpoint
      // For now, show current user info
      setStaff([{
        user_id: user.user_id,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        role: user.role,
        status: 'ACTIVE'
      }]);
    } catch (err) {
      console.error('Failed to load staff:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    setInviteResult(null);
    
    try {
      const result = await api('/auth/invite-staff', {
        method: 'POST',
        body: JSON.stringify({
          email: inviteForm.email,
          first_name: inviteForm.first_name,
          last_name: inviteForm.last_name,
          role: inviteForm.role,
          phone: inviteForm.phone || null
        })
      });
      
      setInviteResult(result);
      toast.success('Staff member invited!');
      loadStaff();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setInviteLoading(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied!`);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const filteredStaff = staff.filter(s => {
    const name = `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase();
    const email = (s.email || '').toLowerCase();
    const term = searchTerm.toLowerCase();
    return name.includes(term) || email.includes(term);
  });

  // Assignable roles (admins can only assign these)
  const assignableRoles = ['CLIENT_ADMIN', 'SCHEDULER', 'RECORD_KEEPER', 'VIEWER'];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-white">Staff & Users</h2>
          <p className="text-slate-400 text-sm">Manage non-driver staff members</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search staff..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm w-full sm:w-64"
              data-testid="staff-search"
            />
          </div>
          <button
            onClick={() => { setShowInvite(true); setInviteResult(null); }}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition text-sm"
            data-testid="invite-staff-btn"
          >
            <UserPlus className="w-4 h-4" />
            Invite Staff
          </button>
        </div>
      </div>

      {/* Role Legend */}
      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
        <h3 className="text-sm font-medium text-white mb-3">Available Roles</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {assignableRoles.map(role => (
            <div key={role} className="flex items-start gap-2">
              <span className={`px-2 py-0.5 rounded text-xs ${ROLE_COLORS[role]}`}>
                {ROLE_LABELS[role]}
              </span>
              <p className="text-slate-500 text-xs flex-1">{ROLE_DESCRIPTIONS[role]}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Invite Modal */}
      {showInvite && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowInvite(false)}>
          <div className="bg-slate-800 rounded-2xl p-5 sm:p-6 max-w-lg w-full border border-slate-700 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Invite Staff Member</h3>
              <button onClick={() => setShowInvite(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {!inviteResult ? (
              <form onSubmit={handleInvite} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">First Name *</label>
                    <input
                      type="text"
                      value={inviteForm.first_name}
                      onChange={(e) => setInviteForm({ ...inviteForm, first_name: e.target.value })}
                      className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                      required
                      data-testid="staff-first-name"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Last Name *</label>
                    <input
                      type="text"
                      value={inviteForm.last_name}
                      onChange={(e) => setInviteForm({ ...inviteForm, last_name: e.target.value })}
                      className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                      required
                      data-testid="staff-last-name"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Email *</label>
                  <input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                    required
                    data-testid="staff-email"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Role *</label>
                  <select
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                    className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                    data-testid="staff-role"
                  >
                    {assignableRoles.map(role => (
                      <option key={role} value={role}>{ROLE_LABELS[role]}</option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">{ROLE_DESCRIPTIONS[inviteForm.role]}</p>
                </div>
                
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Phone (Optional)</label>
                  <input
                    type="tel"
                    value={inviteForm.phone}
                    onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
                    className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                    data-testid="staff-phone"
                  />
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowInvite(false)}
                    className="flex-1 py-2.5 bg-slate-700 text-white rounded-lg text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={inviteLoading}
                    className="flex-1 py-2.5 bg-emerald-500 text-white rounded-lg text-sm disabled:opacity-50"
                    data-testid="submit-staff-invite"
                  >
                    {inviteLoading ? 'Creating...' : 'Create Account'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-emerald-500/20 rounded-xl">
                  <Check className="w-5 h-5 text-emerald-400" />
                  <p className="text-emerald-400 text-sm">Staff member created!</p>
                </div>
                
                <div className="space-y-2">
                  <p className="text-white font-medium">{inviteForm.first_name} {inviteForm.last_name}</p>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs ${ROLE_COLORS[inviteResult.role]}`}>
                    {ROLE_LABELS[inviteResult.role]}
                  </span>
                </div>
                
                {/* Credentials */}
                <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30">
                  <p className="text-amber-400 text-xs mb-2">Login Credentials (share securely)</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300 text-sm">Email:</span>
                      <div className="flex items-center gap-2">
                        <span className="text-white text-sm font-mono">{inviteResult.email}</span>
                        <button onClick={() => copyToClipboard(inviteResult.email, 'Email')} className="text-slate-400 hover:text-white">
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300 text-sm">Password:</span>
                      <div className="flex items-center gap-2">
                        <span className="text-white text-sm font-mono">{inviteResult.temp_password}</span>
                        <button onClick={() => copyToClipboard(inviteResult.temp_password, 'Password')} className="text-slate-400 hover:text-white">
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                <p className="text-slate-500 text-xs">
                  The user should change their password after first login.
                </p>
                
                <button
                  onClick={() => {
                    setShowInvite(false);
                    setInviteForm({ first_name: '', last_name: '', email: '', role: 'VIEWER', phone: '' });
                    setInviteResult(null);
                  }}
                  className="w-full py-2.5 bg-slate-700 text-white rounded-lg text-sm"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Staff List */}
      <div className="bg-slate-800/50 rounded-2xl border border-slate-700 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block sm:hidden divide-y divide-slate-700">
              {filteredStaff.length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-sm">
                  {searchTerm ? 'No staff match your search' : 'No staff members found'}
                </div>
              ) : (
                filteredStaff.map(member => (
                  <div key={member.user_id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-slate-400" />
                        </div>
                        <div>
                          <p className="font-medium text-white text-sm">{member.first_name} {member.last_name}</p>
                          <p className="text-xs text-slate-400">{member.email}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-700">
                      <span className={`px-2 py-1 rounded text-xs ${ROLE_COLORS[member.role]}`}>
                        {ROLE_LABELS[member.role]}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        member.status === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600 text-slate-400'
                      }`}>
                        {member.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Desktop Table View */}
            <table className="w-full hidden sm:table">
              <thead className="bg-slate-700/50">
                <tr className="text-left text-slate-400 text-sm">
                  <th className="px-6 py-4">User</th>
                  <th className="px-6 py-4">Role</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody className="text-white">
                {filteredStaff.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-slate-400">
                      {searchTerm ? 'No staff match your search' : 'No staff members. Click "Invite Staff" to add team members.'}
                    </td>
                  </tr>
                ) : (
                  filteredStaff.map(member => (
                    <tr key={member.user_id} className="border-t border-slate-700 hover:bg-slate-700/30">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                            <span className="text-white font-medium">
                              {member.first_name?.[0]}{member.last_name?.[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{member.first_name} {member.last_name}</p>
                            <p className="text-sm text-slate-400">{member.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-sm ${ROLE_COLORS[member.role]}`}>
                          {ROLE_LABELS[member.role]}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-sm ${
                          member.status === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600 text-slate-400'
                        }`}>
                          {member.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button 
                          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"
                          title="Edit (coming soon)"
                          disabled
                        >
                          <Edit2 className="w-4 h-4 opacity-50" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default StaffPanel;
